import { useState } from 'react';
import { useAuthActions } from '../context/useAuth';
import { Eye, EyeOff } from 'lucide-react';

export function OAuthGateway({ onAuthSuccess }: { onAuthSuccess: (token: string, provider: string) => void }) {
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [mode, setMode] = useState<'email_login' | 'email_register'>('email_login');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showSeed, setShowSeed] = useState(false);

  const { signUp, signIn } = useAuthActions();

  // Poll for dev emails removed for local-first mode

  const handleEmailRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    if (password.length < 8) {
      setErrorMsg("Password must be at least 8 characters long.");
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    setStatusMsg("Registering local sandbox...");

    try {
      await signUp(email, password);
      localStorage.setItem('cf_verified_email', email);
      localStorage.setItem('cf_email_verified', 'true');
      onAuthSuccess('email_vault_token', 'email');
    } catch (err: any) {
      setErrorMsg(err.message || 'An error occurred during registration.');
      setStatusMsg(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setErrorMsg("Please enter your email.");
      return;
    }
    if (!password) {
      setErrorMsg("Please enter your password.");
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    setStatusMsg("Unlocking local sandbox...");

    try {
      await signIn(password);
      localStorage.setItem('cf_verified_email', email);
      localStorage.setItem('cf_email_verified', 'true');
      onAuthSuccess('email_vault_token', 'email');
    } catch (err: any) {
      setErrorMsg(err.message || 'Invalid email or password.');
      setStatusMsg(null);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="space-y-6 w-full animate-in fade-in zoom-in duration-200">
        <div className="p-5 rounded-2xl bg-emerald-950/30 border border-emerald-500/20 text-left text-emerald-300 space-y-3">
          <div className="flex items-center gap-2 text-xs font-bold text-emerald-400">
            <span className="text-base">📧</span>
            <span>VERIFICATION LINK DISPATCHED</span>
          </div>
          <p className="text-xs text-emerald-400/90 leading-relaxed">
            We have dispatched a secure authentication verification link to <span className="font-semibold text-white underline">{email}</span>. Please open the link in your inbox to verify your email and unlock your sandbox container.
          </p>
        </div>



        <button
          type="button"
          onClick={() => {
            setEmailSent(false);
            setMode('email_login');
            setStatusMsg(null);
            setErrorMsg(null);
          }}
          className="w-full py-3 bg-[#0c0c0f] border border-white/[0.06] hover:bg-[#121216] text-slate-300 text-xs font-bold rounded-xl uppercase tracking-wider transition-all"
        >
          ← Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full">
      {errorMsg && (
        <div className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-450 rounded-xl text-[10px] uppercase font-bold tracking-wider text-center animate-shake">
          ⚠️ {errorMsg}
        </div>
      )}

      {statusMsg && (
        <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 rounded-xl text-[10px] uppercase font-bold tracking-wider text-center animate-pulse">
          ℹ️ {statusMsg}
        </div>
      )}

      <form
        onSubmit={mode === 'email_login' ? handleEmailLogin : handleEmailRegister}
        className="space-y-4 text-left animate-in fade-in slide-in-from-bottom-4 duration-200"
      >
        <h4 className="font-serif-editorial text-lg text-white font-normal text-center">
          {mode === 'email_login' ? 'Sign In' : 'Sign Up'} using Email
        </h4>

        <div className="space-y-1.5">
          <label className="text-[9px] uppercase font-bold tracking-wider text-slate-400">
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            disabled={loading}
            className="w-full px-4 py-2.5 bg-black border border-white/[0.08] rounded-xl text-xs text-slate-200 placeholder-slate-650 focus:outline-none focus:border-indigo-500 transition-colors"
            required
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-[9px] uppercase font-bold tracking-wider text-slate-400">
            {email.toLowerCase() === 'nisiryancruz@gmail.com' ? '12-Word Master Seed Phrase Vault Key' : 'Vault Passcode / Password'}
          </label>
          <div className="relative flex items-center">
            {email.toLowerCase() === 'nisiryancruz@gmail.com' ? (
              <textarea
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your 12-word recovery seed phrase separated by spaces..."
                disabled={loading}
                rows={3}
                className="w-full pl-4 pr-10 py-2.5 bg-black border border-white/[0.08] rounded-xl text-xs text-slate-200 placeholder-slate-650 focus:outline-none focus:border-indigo-500 transition-colors resize-none"
                style={{ WebkitTextSecurity: showSeed ? 'none' : 'disc' } as any}
                required
              />
            ) : (
              <input
                type={showSeed ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                disabled={loading}
                className="w-full pl-4 pr-10 py-2.5 bg-black border border-white/[0.08] rounded-xl text-xs text-slate-200 placeholder-slate-650 focus:outline-none focus:border-indigo-500 transition-colors"
                required
              />
            )}
            <button
              type="button"
              onClick={() => setShowSeed(!showSeed)}
              className="absolute right-3 text-slate-400 hover:text-slate-200 transition-colors focus:outline-none"
              style={email.toLowerCase() === 'nisiryancruz@gmail.com' ? { top: '10px' } : undefined}
            >
              {showSeed ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {mode === 'email_register' && email.toLowerCase() !== 'nisiryancruz@gmail.com' && (
            <p className="text-[9px] text-slate-500">
              Must be at least 8 characters. This password is used to encrypt your database locally.
            </p>
          )}
          {email.toLowerCase() === 'nisiryancruz@gmail.com' && (
            <p className="text-[9px] text-indigo-400 font-semibold">
              ⚠️ Administrator console seed phrase validation active.
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl text-xs uppercase tracking-wider shadow-lg transition-all active:scale-[0.98] disabled:opacity-50"
        >
          {loading ? 'Processing...' : mode === 'email_login' ? 'Sign In' : 'Sign Up & Send Link'}
        </button>

        <div className="flex items-center justify-between text-[11px] pt-2">
          <button
            type="button"
            onClick={() => {
              setMode(mode === 'email_login' ? 'email_register' : 'email_login');
              setErrorMsg(null);
            }}
            className="text-indigo-400 hover:underline w-full text-center font-semibold"
          >
            {mode === 'email_login' ? "Need an account? Sign Up" : "Already have an account? Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
}
